import React, { useState } from "react";
import { Chip, Popover, Box, Typography, Divider } from "@mui/material";
import { Timelapse, EventBusy } from "@mui/icons-material";

/**
 * Visual indicator rendered on timetable slots affected by a lecture extension.
 *
 * For extended slots (isExtended): shows the new end time and that the slot duration
 * was increased.
 *
 * For cancelled slots (isCancelledByExtend): shows which teacher consumed this slot
 * and the source slot details.
 *
 * Props:
 * - isExtended: boolean — the source slot has been extended
 * - isCancelledByExtend: boolean — this slot was cancelled/consumed by an extension
 * - extendRequestId: string|null — UUID of the extend request
 * - extendedEndTime: string|null — new end time for extended slots (e.g. "15:00")
 * - cancelledByTeacher: string|null — name of the extending teacher (for cancelled slots)
 * - cancelledBySlot: object|null — { startTime, endTime, subjectName } of the source slot
 */
const ExtendBadge = ({
  isExtended,
  isCancelledByExtend,
  extendRequestId,
  extendedEndTime,
  cancelledByTeacher,
  cancelledBySlot,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  if (!isExtended && !isCancelledByExtend) return null;

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Determine badge appearance based on type
  const isCancelled = isCancelledByExtend && !isExtended;

  return (
    <>
      <Chip
        icon={
          isCancelled
            ? <EventBusy sx={{ fontSize: 14 }} />
            : <Timelapse sx={{ fontSize: 14 }} />
        }
        label={isCancelled ? "Cancelled" : "Extended"}
        size="small"
        color={isCancelled ? "error" : "success"}
        variant="filled"
        onClick={handleClick}
        sx={{
          height: 20,
          fontSize: "0.65rem",
          cursor: "pointer",
          "& .MuiChip-icon": { fontSize: 14 },
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{ paper: { sx: { p: 2, maxWidth: 300 } } }}
      >
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {isCancelled ? "Slot Cancelled by Extension" : "Lecture Extended"}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        {isExtended && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="body2">
              This lecture has been extended.
            </Typography>
            {extendedEndTime && (
              <Typography variant="body2">
                <strong>New End Time:</strong> {extendedEndTime}
              </Typography>
            )}
          </Box>
        )}
        {isCancelled && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="body2">
              This slot was consumed by a lecture extension.
            </Typography>
            {cancelledByTeacher && (
              <Typography variant="body2">
                <strong>Extended by:</strong> {cancelledByTeacher}
              </Typography>
            )}
            {cancelledBySlot && (
              <>
                <Typography variant="caption" fontWeight={600} sx={{ mt: 0.5 }}>
                  Source Slot:
                </Typography>
                <Typography variant="caption">
                  {cancelledBySlot.startTime} – {cancelledBySlot.endTime}
                  {cancelledBySlot.subjectName && ` (${cancelledBySlot.subjectName})`}
                </Typography>
              </>
            )}
          </Box>
        )}
        {extendRequestId && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Request: {extendRequestId.slice(0, 8)}…
          </Typography>
        )}
      </Popover>
    </>
  );
};

export default ExtendBadge;
